import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose, setPropTypes, withState, withHandlers } from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import { max } from 'lodash';
import {
  Button,
  CardMedia,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@material-ui/core';
import { bindActionCreators } from 'redux';
import { sendLogin, uploadFile } from '../../auth/Auth';
import {
  editShopBankAccount,
  EDIT_SHOP_BANK_ACCOUNT_SUCCESS
} from '../../actions/Shop';
import { reIdentity } from '../../actions/Accounts';
import { editIdentity, EDIT_IDENTITY_SUCCESS } from '../../actions/Identity';
import ProgressDialog from '../../components/molecules/ProgressMobileDialog';
import StepProgressBar from '../../components/atoms/StepProgressBar';

const styles = theme => ({
  hidden: {
    display: 'none'
  },
  page: {
    paddingLeft: 15,
    paddingRight: 15
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
    paddingTop: theme.spacing(1)
  },
  titleGroup: {
    marginTop: 20,
    marginBottom: 20,
    display: 'flex',
    alignItems: 'flex-start'
  },
  pagerInDiv: {
    padding: 20
  },
  redText: {
    color: '#FF0000'
  },
  tableTitle: {
    fontSize: 16,
    marginBottom: '20px'
  },
  detailTable: {
    width: '100%'
  },
  tableCellItem: {
    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#F7F7F7',
    width: '115px',
    padding: '13px 8px',
    fontSize: 12,
    fontWeight: 700
  },
  tableCellData: {
    borderBottom: '1px solid #E0E0E0',
    padding: '13px 8px',
    fontSize: 12,
    fontWeight: 500
  },
  noteText: {
    fontSize: 12,
    paddingTop: 10,
    fontWeight: 600
  },
  textLabel: {
    fontSize: 12,
    fontWeight: 600
  },
  description: {
    color: '#707070',
    fontSize: 14
  },
  mb10: {
    marginBottom: 10
  },
  alignItemsStart: {
    alignItems: 'start'
  },
  linkToPrev: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 20
  },
  image: {
    marginBottom: 5
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      isReIdentity: state.accounts.reIdentity,
      identities: state.identities.identities[0]
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          editShopBankAccount,
          editIdentity,
          reIdentity
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('isRegisterSuccess', 'updateIsRegisterSuccess', false),
  withState('openRegisterProgress', 'updateOpenRegisterProgress', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('titleMessage', 'setTitleMessage', ''),
  withState('btnText', 'setBtnText', null),
  withState('previewImageRegisterCopy', 'setPreviewImageRegisterCopy', null),
  withState('previewImageLicense', 'setPreviewImageLicense', []),
  withState('previewImageSeal', 'setPreviewImageSeal', []),
  withState('agreeOne', 'setAgreeOne', false),
  withState('agreeTwo', 'setAgreeTwo', false),
  withState('agreeThree', 'setAgreeThree', false),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    show,
    proceedToNext,
    handleBack,
    isReIdentity,
    shopId,
    identityId,
    shopParams,
    identityParams,
    previewImageRegisterCopy,
    setPreviewImageRegisterCopy,
    previewImageLicense,
    setPreviewImageLicense,
    previewImageSeal,
    setPreviewImageSeal,
    isRegisterSuccess,
    updateIsRegisterSuccess,
    openRegisterProgress,
    updateOpenRegisterProgress,
    loading,
    updateLoading,
    resultMessage,
    setResultMessage,
    titleMessage,
    setTitleMessage,
    btnText,
    setBtnText,
    agreeOne,
    setAgreeOne,
    agreeTwo,
    setAgreeTwo,
    agreeThree,
    setAgreeThree,
    identities
  }) => {
    useEffect(() => {
      if (show) {
        if (identityParams.registerCopy) {
          const reader = new FileReader();
          reader.readAsDataURL(identityParams.registerCopy);
          reader.onloadend = () => {
            setPreviewImageRegisterCopy(reader.result);
          };
        }
        if (identityParams.businessLicense) {
          // Remove empty items
          const businessLicenseList = identityParams.businessLicense.filter(
            i => i
          );
          const loadImgPromises = [];
          for (let i = 0; i < businessLicenseList.length; i += 1) {
            loadImgPromises.push(
              new Promise(resolve => {
                const reader = new FileReader();
                reader.readAsDataURL(businessLicenseList[i]);
                reader.onloadend = () => {
                  resolve(reader.result);
                };
              })
            );
          }
          Promise.all(loadImgPromises).then(imgs => {
            setPreviewImageLicense(imgs);
          });
        }

        if (identityParams.sealCertificate) {
          // Remove empty items
          const sealCertificateList = identityParams.sealCertificate.filter(
            i => i
          );
          const loadImgPromises = [];
          for (let i = 0; i < sealCertificateList.length; i += 1) {
            loadImgPromises.push(
              new Promise(resolve => {
                const reader = new FileReader();
                reader.readAsDataURL(sealCertificateList[i]);
                reader.onloadend = () => {
                  resolve(reader.result);
                };
              })
            );
          }
          Promise.all(loadImgPromises).then(imgs => {
            setPreviewImageSeal(imgs);
          });
        }
      }
    }, [show]);

    const registerAction = async () => {
      updateLoading(true);
      updateOpenRegisterProgress(true);

      const editIdentityParams = {};
      let sealPath = null;
      let licensePath = null;
      let registerCopyPath = null;

      try {
        // Params from step 1.1
        if (!isReIdentity) {
          const editShopBankAccountResult = await actions
            .editShopBankAccount(shopId, shopParams)
            .then(data => checkSession(data));
          if (
            editShopBankAccountResult.type !== EDIT_SHOP_BANK_ACCOUNT_SUCCESS
          ) {
            throw new Error('edit shop bank account failure');
          }
          if (identityParams.registerCopy) {
            registerCopyPath = await uploadFile(
              'identity',
              identityId,
              identityParams.registerCopy,
              'public'
            );
            editIdentityParams.registeredCopy = registerCopyPath;
          }
          editIdentityParams.note = identityParams.note;
        } else {
          registerCopyPath = identities ? identities.registeredCopy : '';
        }

        // Params from step 1.2
        if (identityParams.sealCertificate && identityParams.businessLicense) {
          // Remove empty items
          const sealCertificateList = identityParams.sealCertificate.filter(
            i => i
          );
          const businessLicenseList = identityParams.businessLicense.filter(
            i => i
          );

          if (sealCertificateList.length > 0) {
            const imagePaths = await Promise.all(
              sealCertificateList.map(async image => {
                if (image !== null) {
                  // if (image.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
                  //   const { resizeImageFile } = await resizeImage(image);
                  //   image = resizeImageFile;
                  // }
                  const imagePath = await uploadFile(
                    'identity',
                    identityId,
                    image,
                    'public'
                  );
                  return imagePath;
                }
                return null;
              })
            );
            sealPath = imagePaths
              .filter(i => i !== null)
              .map((i, index) => {
                return { image: i, viewOrder: index };
              });
          }
          if (businessLicenseList.length > 0) {
            const imagePaths = await Promise.all(
              businessLicenseList.map(async image => {
                if (image !== null) {
                  const imagePath = await uploadFile(
                    'identity',
                    identityId,
                    image,
                    'public'
                  );
                  return imagePath;
                }
                return null;
              })
            );
            licensePath = imagePaths
              .filter(i => i !== null)
              .map((i, index) => {
                return { image: i, viewOrder: index };
              });
          }

          const identityImages = [];
          if (registerCopyPath) {
            identityImages.push({
              registeredCopy: registerCopyPath || ''
            });
          }
          const maxLengthImage = max([
            sealCertificateList.length,
            businessLicenseList.length
          ]);
          if (maxLengthImage) {
            for (let i = 0; i < maxLengthImage; i += 1) {
              identityImages.push({
                sealCertificate: sealPath[i] ? sealPath[i].image : '',
                other: licensePath[i] ? licensePath[i].image : ''
              });
            }
          }
          editIdentityParams.identityImages = identityImages;
        }

        editIdentityParams.numberIdentity = identityParams.numberIdentity;
        const editIdentityResult = await actions
          .editIdentity(identityId, editIdentityParams)
          .then(data => checkSession(data));
        if (editIdentityResult.type !== EDIT_IDENTITY_SUCCESS) {
          throw new Error('edit identity failure');
        }
        // If no error then show success message
        updateIsRegisterSuccess(true);
        setTitleMessage('審査書類提出完了');
        setResultMessage(
          <>
            審査書類の提出が完了しました。事務局にて審査を行いますので、いましばらくお待ちください。審査結果はメールで通知いたします。
            <br />
            審査が完了するまでの間に、
            <br />
            ・店舗情報登録
            <br />
            ・メニュー登録
            <br />
            が可能ですので、ぜひ情報を充実いただけますよう、お願いいたします。
          </>
        );
        setBtnText('各種情報を入力する');
      } catch (err) {
        setResultMessage('登録に失敗しました。');
      }
      updateLoading(false);
    };

    const progressOkAction = () => {
      if (isRegisterSuccess) {
        // TODO: action.getIdentites()
        actions.reIdentity(false);
        proceedToNext();
      }
      updateOpenRegisterProgress(false);
    };

    return (
      <div className={show ? null : classes.hidden}>
        <div className={classes.page}>
          <div className={classes.titleGroup}>
            <Typography component="h2" variant="h5">
              審査申請情報の確認
            </Typography>
          </div>

          <StepProgressBar currentStep={2} />

          <Paper className={classes.paper} elevation={1}>
            <div className={classes.pagerInDiv}>
              <p className={classes.tableTitle}>
                審査申請情報をご確認ください。
              </p>
              <Table className={classes.detailTable}>
                <TableBody>
                  {!isReIdentity && (
                    <>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          銀行名
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {shopParams.bankName}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          支店名
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {shopParams.bankBranchName}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          口座種別
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {shopParams.bankAccountType === 'SAVINGS'
                            ? '普通預金'
                            : '当座預金'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          口座番号
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {shopParams.bankAccountNumber}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          口座名義
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {shopParams.bankAccountHolder}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          振込先銀行通帳の1ページ目
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          <CardMedia
                            component="img"
                            image={previewImageRegisterCopy}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCellItem}>
                          連絡欄
                        </TableCell>
                        <TableCell className={classes.tableCellData}>
                          {identityParams.note}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                  {identityParams.businessLicense && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        営業許可証
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {previewImageLicense.map(item => (
                          <CardMedia
                            component="img"
                            image={item}
                            className={classes.image}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  {identityParams.sealCertificate && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        印鑑証明 / 公共 料金の領収書 / 身分証明書
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {previewImageSeal.map(item => (
                          <CardMedia
                            component="img"
                            image={item}
                            className={classes.image}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  {typeof identityParams.numberIdentity !== 'undefined' && (
                    <TableRow>
                      <TableCell className={classes.tableCellItem}>
                        法人番号
                      </TableCell>
                      <TableCell className={classes.tableCellData}>
                        {identityParams.numberIdentity}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className={classes.pagerInDiv}>
              <div className={classes.mb10}>
                <FormControlLabel
                  className={classes.alignItemsStart}
                  control={
                    <Checkbox
                      checked={agreeOne}
                      color="primary"
                      onChange={e => {
                        setAgreeOne(e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Typography className={classes.font12}>
                      GOCHIプラットフォームに登録した情報と一致する本人確認書類を、上記のとおり揃え、登録します。
                    </Typography>
                  }
                />
              </div>
              <div className={classes.mb10}>
                <FormControlLabel
                  className={classes.alignItemsStart}
                  control={
                    <Checkbox
                      checked={agreeTwo}
                      color="primary"
                      onChange={e => {
                        setAgreeTwo(e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Typography className={classes.font12}>
                      風俗営業の1号営業〜4号営業、性風俗関連特殊営業は行っていません。
                      <br />
                      風営適正化法による許可・届出をしている場合は、その営業許可証をアップロードし、登録します。
                    </Typography>
                  }
                />
              </div>
              <div>
                <FormControlLabel
                  className={classes.alignItemsStart}
                  control={
                    <Checkbox
                      checked={agreeThree}
                      color="primary"
                      onChange={e => {
                        setAgreeThree(e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Typography className={classes.font12}>
                      虚偽の申請・営業実態が異なる場合、サービス利用中であっても登録取消になることに同意します。
                    </Typography>
                  }
                />
              </div>
            </div>
            <div className={classes.pagerInDiv}>
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                onClick={registerAction}
                disabled={!agreeOne || !agreeTwo || !agreeThree}
              >
                審査申請する
              </Button>
            </div>
          </Paper>
        </div>
        <div className={classes.linkToPrev}>
          <Button color="primary" onClick={handleBack}>
            前の画面に戻る
          </Button>
        </div>
        <ProgressDialog
          openProgress={openRegisterProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
          title={titleMessage}
          btnText={btnText}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
